<template>
  <LandingBackground>
    <div class="mb-20">
      <div class="mx-auto mt-10 grid grid-cols-1 items-start gap-10 sm:px-5 md:px-8 xl:grid-cols-2 3xl:grid-cols-3">
        <div class="flex min-w-0 flex-col gap-10">
          <LandingCard variant="info" :title="`Welcome, ${userRef.firstName} ${userRef.lastName}`">
            <template #default>
              <LandingCardLinkSections label="Distributors">
                <LandingCardLinkSection
                  link-label="Find Distributors"
                  description="Find, view, and manage distributors who import or distribute deposit beverage containers."
                  :to="{ name: routeNames.distributor.list }"
                />
              </LandingCardLinkSections>
              <LandingCardLinkSections label="Distributor Reports">
                <LandingCardLinkSection
                  link-label="Find Distributor Reports"
                  description="Find, view, and manage monthly and semi-annual distributor reports."
                  :to="{ name: routeNames.distributorReport.list }"
                />
              </LandingCardLinkSections>
            </template>
          </LandingCard>
        </div>
        <div class="flex min-w-0 flex-col gap-10 xl:col-start-2 3xl:contents">
          <div class="flex min-w-0 flex-col gap-10 3xl:col-start-2">
            <LandingCard
              title="Distributor Reports"
              :view-all-to="{
                name: routeNames.distributorReport.list,
                query: { statusMurnIn: [], paymentStatusMurnIn: [] },
              }"
            >
              <LandingCardSkeleton v-if="areReportsLoading" />
              <div
                class="bg-neutral-50 p-10 sm:rounded-b-lg"
                v-if="!areReportsLoading && !pastDueReports?.length && !submittedReports?.length"
              >
                <NoneFoundFlag label="No Past Due or Submitted Distributor Reports" />
              </div>
              <LandingCardEntityList
                title="Past Due &ndash; 5 oldest"
                :items="pastDueReports"
                variant="warn"
                :view-all-to="{
                  name: routeNames.distributorReport.list,
                  query: { statusMurnIn: [LE.ReportStatus.PastDue] },
                }"
              >
                <template #default="{ item }">
                  <LandingCardEntityListItem>
                    <template #primaryLabel>
                      <span v-if="item.frequencyMurn === LE.ReportFrequency.SemiAnnual">CY </span>
                      {{ item.year }}
                      <LookupDescription :murn="item.periodMurn" />
                      <SingleSpace />
                      <LookupDescription :murn="item.typeMurn" />
                    </template>
                    <template #secondaryLabel>
                      {{ item.distributor?.name }}
                    </template>
                    <template #additionalInfo>
                      <div
                        class="mt-1.5 text-sm text-neutral-400 md:mt-0 md:self-center md:pr-4 md:text-right"
                        v-if="item.dueDate"
                      >
                        <span class="md:block"
                          >Due <span class="text-neutral-500">{{ sysDate.shortDateString(item.dueDate) }}</span></span
                        >
                      </div>
                    </template>
                  </LandingCardEntityListItem>
                </template>
              </LandingCardEntityList>

              <LandingCardEntityList
                title="Submitted &ndash; 5 newest"
                :items="submittedReports"
                :view-all-to="{
                  name: routeNames.distributorReport.list,
                  query: { statusMurnIn: [LE.ReportStatus.Submitted] },
                }"
              >
                <template #default="{ item }">
                  <LandingCardEntityListItem>
                    <template #primaryLabel>
                      <LookupDescription :murn="item.frequencyMurn" />
                      <SingleSpace />
                      <LookupDescription :murn="item.typeMurn" />
                    </template>
                    <template #secondaryLabel>
                      {{ item.distributor?.name }}
                    </template>
                    <template #additionalInfo>
                      <div
                        class="mt-1.5 text-sm text-neutral-400 md:mt-0 md:self-center md:pr-4 md:text-right"
                        v-if="item.receivedDate"
                      >
                        <span class="md:block"
                          >Received
                          <span class="text-neutral-500">{{ sysDate.shortDateString(item.receivedDate) }}</span></span
                        >
                      </div>
                    </template>
                  </LandingCardEntityListItem>
                </template>
              </LandingCardEntityList>
            </LandingCard>
          </div>
          <div class="flex min-w-0 flex-col gap-10 3xl:col-start-3">
            <LandingCard
              title="Distributors"
              :view-all-to="{ name: routeNames.distributor.list, query: { statusMurnIn: [] } }"
            >
              <template #default>
                <div class="py-5 pl-4 sm:pl-6">
                  <PublicLandingList>
                    <PublicLandingListItem>
                      <router-link
                        class="text-sky-600 hover:underline"
                        :to="{
                          name: routeNames.distributor.list,
                          query: { reportFrequencyMurnIn: ['murn:odr:lookup/DistributorReportFrequency/Monthly'] },
                        }"
                      >
                        View Monthly Distributors</router-link
                      >
                    </PublicLandingListItem>

                    <PublicLandingListItem>
                      <router-link
                        class="text-sky-600 hover:underline"
                        :to="{
                          name: routeNames.distributor.list,
                          query: { reportFrequencyMurnIn: ['murn:odr:lookup/DistributorReportFrequency/SemiAnnual'] },
                        }"
                      >
                        View Semi-Annual Reporters</router-link
                      >
                    </PublicLandingListItem>

                    <PublicLandingListItem>
                      <router-link
                        class="text-sky-600 hover:underline"
                        :to="{
                          name: routeNames.distributor.list,
                          query: { statusMurnIn: ['murn:odr:lookup/DistributorStatus/Active'] },
                        }"
                      >
                        View Active Distributors</router-link
                      >
                    </PublicLandingListItem>

                    <PublicLandingListItem>
                      <router-link
                        class="text-sky-600 hover:underline"
                        :to="{
                          name: routeNames.distributor.list,
                          query: { statusMurnIn: ['murn:odr:lookup/DistributorStatus/Closed'] },
                        }"
                      >
                        View Closed Distributors</router-link
                      >
                    </PublicLandingListItem>

                    <PublicLandingListItem>
                      <router-link
                        class="text-sky-600 hover:underline"
                        :to="{
                          name: routeNames.distributor.list,
                          query: { containerMaterialTypes: ['murn:odr:lookup/ContainerMaterialType/Aluminum'] },
                        }"
                      >
                        View Aluminum Distributors</router-link
                      >
                    </PublicLandingListItem>

                    <PublicLandingListItem>
                      <router-link
                        class="text-sky-600 hover:underline"
                        :to="{
                          name: routeNames.distributor.list,
                          query: { containerMaterialTypes: ['murn:odr:lookup/ContainerMaterialType/BiMetal'] },
                        }"
                      >
                        View Bi-Metal Distributors</router-link
                      >
                    </PublicLandingListItem>

                    <PublicLandingListItem>
                      <router-link
                        class="text-sky-600 hover:underline"
                        :to="{
                          name: routeNames.distributor.list,
                          query: {
                            containerMaterialTypes: [
                              'murn:odr:lookup/ContainerMaterialType/PlasticPETE',
                              'murn:odr:lookup/ContainerMaterialType/PlasticHDPE',
                            ],
                          },
                        }"
                      >
                        View Plastic Distributors</router-link
                      >
                    </PublicLandingListItem>

                    <PublicLandingListItem>
                      <router-link
                        class="text-sky-600 hover:underline"
                        :to="{
                          name: routeNames.distributor.list,
                          query: { containerMaterialTypes: ['murn:odr:lookup/ContainerMaterialType/Glass'] },
                        }"
                      >
                        View Glass Distributors</router-link
                      >
                    </PublicLandingListItem>
                  </PublicLandingList>
                </div>
              </template>
            </LandingCard>
          </div>
        </div>
      </div>
    </div>
  </LandingBackground>
</template>

<script lang="ts">
import type { LandingCardEntity } from "@/components/landing/landingCard";
import LandingCard from "@/components/landing/LandingCard.vue";
import LandingCardEntityList from "@/components/landing/LandingCardEntityList.vue";
import LandingCardEntityListItem from "@/components/landing/LandingCardEntityListItem.vue";
import LandingCardLinkSection from "@/components/landing/LandingCardLinkSection.vue";
import LandingCardLinkSections from "@/components/landing/LandingCardLinkSections.vue";
import LandingCardSkeleton from "@/components/landing/LandingCardSkeleton.vue";
import LandingBackground from "@/components/LandingBackground.vue";
import { LE } from "@/constants/enums";
import { routeNames } from "@/routes";
import { getDistributorReports } from "@/services/endpoints";
import type { DistributorReport } from "@/views/distributorReport/distributorReport";
import distributorReportService from "@/views/distributorReport/distributorReportService";
import { NoneFoundFlag, SingleSpace, useAppSettingsStore } from "@modernary/mui/components/common";
import { adaptPagedResponse, LookupDescription } from "@modernary/mui/components/lookup";
import {
  PublicLandingCardSection,
  PublicLandingList,
  PublicLandingListItem,
} from "@modernary/mui/components/publicLanding";
import { useToastStore } from "@modernary/mui/components/toasts";
import { useApi, type Paging } from "@modernary/mui/utils/api";
import { useUser } from "@modernary/mui/utils/authentication";
import { sysDate } from "@modernary/mui/utils/filters";
import { AxiosError } from "axios";
import { computed, defineComponent, ref, watch } from "vue";

export default defineComponent({
  name: "HomeInternal",
  components: {
    LandingBackground,
    LandingCard,
    LandingCardLinkSection,
    LandingCardLinkSections,
    LandingCardEntityList,
    LandingCardEntityListItem,
    LandingCardSkeleton,
    LookupDescription,
    NoneFoundFlag,
    PublicLandingList,
    PublicLandingListItem,
    PublicLandingCardSection,
    SingleSpace,
  },
  setup() {
    const { userRef } = useUser();
    const appSettingsStore = useAppSettingsStore();
    const toastStore = useToastStore();
    const areReportsLoading = ref(2);

    const pastDueReports = ref<LandingCardEntity<DistributorReport>[]>();
    getDistributorReports({
      statusMurnIn: [LE.ReportStatus.PastDue],
      orderBy: [{ propertyName: "dueDate", isDescending: false }],
      take: 5,
    })
      .then(async (response) => {
        const data = (await adaptPagedResponse(
          response,
          distributorReportService.responseAdapter as any,
        )) as unknown as Paging<LandingCardEntity<DistributorReport>>;
        data._items.forEach((item) => {
          item.to = { name: routeNames.distributorReport.view, params: { id: item.id } };
        });
        pastDueReports.value = data._items;
      })
      .catch((error) => {
        if (error instanceof AxiosError) {
          toastStore.toastError((options) => {
            options.title = "Could not get Distributor Reports";
          }, error.status || 500);
        }
      })
      .finally(() => {
        areReportsLoading.value--;
      });

    const submittedReports = ref<LandingCardEntity<DistributorReport>[]>();
    getDistributorReports({
      statusMurnIn: [LE.ReportStatus.Submitted],
      orderBy: [{ propertyName: "receivedDate", isDescending: true }],
      take: 5,
    })
      .then(async (response) => {
        const data = (await adaptPagedResponse(
          response,
          distributorReportService.responseAdapter as any,
        )) as unknown as Paging<LandingCardEntity<DistributorReport>>;
        data._items.forEach((item) => {
          item.to = { name: routeNames.distributorReport.view, params: { id: item.id } };
        });
        submittedReports.value = data._items;
      })
      .catch((error) => {
        if (error instanceof AxiosError) {
          toastStore.toastError((options) => {
            options.title = "Could not get Distributor Reports";
          }, error.status || 500);
        }
      })
      .finally(() => {
        areReportsLoading.value--;
      });

    return {
      pastDueReports,
      submittedReports,
      userRef,
      routeNames,
      appSettingsStore,
      sysDate,
      areReportsLoading,
      LE,
    };
  },
});
</script>
